import { Typename } from '~/contentful/_generated/typename';
import { type Masked, unmask } from '~/contentful/graphql';
import { flattenCollection } from '~/contentful/utils/collection';
import { AnalyticsCard, AnalyticsContent } from '~/ui/components/analytics';
import { BaseCard, type CardSize, type CommonCardProps } from '~/ui/components/cards/base';
import { type Image_AssetFragment } from '~/ui/modules/image/query';
import { getInternalLinkFromFragment } from '~/ui/systems/internalLink';

import {
  type AnnualReportCard_AnnualReportFragment,
  type AnnualReportCard_OldAnnualReportFragment,
} from './query';

type AnnualReportFragment =
  | typeof AnnualReportCard_AnnualReportFragment
  | typeof AnnualReportCard_OldAnnualReportFragment;

export interface AnnualReportCardProps extends CommonCardProps {
  data: Masked<AnnualReportFragment>;
  size: CardSize;
  /** @default "Read the report" */
  cta?: string;
}

export function AnnualReportCard({
  data,
  size,
  cta = 'Read the report',
  module,
  ...props
}: AnnualReportCardProps) {
  const annualReport = unmask<AnnualReportFragment>(data);

  let image: Masked<typeof Image_AssetFragment> | undefined;
  switch (annualReport.__typename) {
    case Typename.ANNUAL_REPORT_PAGE:
      image = flattenCollection(annualReport.introImagesCollection)[0];
      break;
    case Typename.ANNUAL_REPORT_PDF:
      image = undefined;
      break;
  }

  return (
    <BaseCard
      title={`${annualReport.year} Annual Report`}
      cta={cta}
      eyebrow="Report"
      href={getInternalLinkFromFragment(annualReport)}
      hero={image}
      size={size}
      event={{
        cardType: AnalyticsCard.ANNUAL_REPORT,
        cardContentType: AnalyticsContent.ANNUAL_REPORT,
        cardModule: module,
      }}
      {...props}
    />
  );
}
